import type {OperationGroup} from 'components/customer/Preview3D/types';

type ShelfStyleValues = {
    cabinet_simple_shelves?: number;
    simple_shelf_type?: number;
};

export const makeZeroIfUndefined = (value: string | number): number => {
    if (typeof value === 'undefined' || value === null) return 0;

    return Number(value);
};

export const convertShelfPositionToPercentage = (
    value: number,
    height: number
): number => {
    if (value > 1) return value / height;

    return value;
};

export enum ShelfStyle {
    FIXED = 0,
    ADJUSTABLE = 1,
    SYSTEM_32 = 2,
    CONTINUOUS = 3,
}

// System 32 drilling always sets shelves back 5mm, whatever the room default is.
export const SYSTEM_32_SHELF_SET_BACK = 5;

const DEFAULT_SHELF_SET_BACK = 5;

type SimpleShelfSetBackValues = {
    simple_shelf_type?: number;
    shelf_set_back?: number | string | null;
    shelves?: {shelf_offset?: number | string | null}[];
};

const toSetBack = (
    value: number | string | null | undefined
): number | null => {
    if (value === null || typeof value === 'undefined' || value === '')
        return null;

    const setBack = Number(value);

    return Number.isFinite(setBack) ? setBack : null;
};

/**
 * The setback of a simple (non-advanced) shelf, mirroring the backend: fixed for
 * System 32 drilling, otherwise the room default. A saved shelf's offset stands in
 * where the room default is not supplied (e.g. the standalone preview).
 *
 * @param {SimpleShelfSetBackValues} values Product form values
 * @return {number} Setback in mm
 */
export const getSimpleShelfSetBack = (
    values: SimpleShelfSetBackValues
): number => {
    if (Number(values?.simple_shelf_type) === ShelfStyle.SYSTEM_32)
        return SYSTEM_32_SHELF_SET_BACK;

    return (
        toSetBack(values?.shelf_set_back) ??
        toSetBack(values?.shelves?.[0]?.shelf_offset) ??
        DEFAULT_SHELF_SET_BACK
    );
};

export const getShelfStyle = (shelfStyle: number): string => {
    switch (Number(shelfStyle)) {
        case ShelfStyle.SYSTEM_32:
            return 'System32';
        case ShelfStyle.CONTINUOUS:
            return 'Continuous';
        case ShelfStyle.ADJUSTABLE:
            return 'Adj';
        case ShelfStyle.FIXED:
        default:
            return 'Fixed';
    }
};

export const getShelfStyleLabel = (
    values: ShelfStyleValues,
    shelfStyle?: number | null
): string => {
    const useShelfStyle =
        !values?.cabinet_simple_shelves &&
        typeof shelfStyle !== 'undefined' &&
        shelfStyle !== null;

    return getShelfStyle(
        useShelfStyle ? shelfStyle : values?.simple_shelf_type
    );
};

// z of the pantry duplicate box-top, or null when not applicable.
export const getPantryDuplicateBoxTopZ = (
    isPantry: boolean,
    partitionHeight: number | string | undefined,
    carcaseThickness: number
): number | null => {
    if (!isPantry || typeof partitionHeight === 'undefined') return null;

    return Number(partitionHeight) - carcaseThickness;
};

// True for the unlabelled box-top that duplicates the Horizontal Partition.
export const isPantryDuplicateBoxTop = (
    operationGroup: OperationGroup,
    pantryBoxTopZ: number | null
): boolean => {
    if (pantryBoxTopZ === null || operationGroup.ShelfLabel) return false;

    const z = operationGroup.Operations?.[0]?.Insert?.z;

    return typeof z === 'number' && Math.abs(z - pantryBoxTopZ) < 1;
};
